import React, { useCallback, useEffect, useMemo, useState } from "react";
import { api, fmtDate, fmtMoney, formatApiErrorDetail } from "@/lib/apiClient";
import { PageHeader, EmptyState, BcRibbonGroup, BcRibbonBtn } from "@/components/Shared";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ArrowsClockwise, ChartLine, Warning, TrendUp, TrendDown, Money, Buildings, ArrowRight } from "@phosphor-icons/react";
import { toast } from "sonner";

// ---------- helpers ----------
function KPI({ label, value, tone = "slate", hint, testid }) {
  const cls = {
    slate: "bg-white border-slate-200 text-slate-900",
    dark: "bg-[#0F172A] text-white border-[#0F172A]",
    orange: "bg-[#EA580C] text-white border-[#EA580C]",
    emerald: "bg-emerald-600 text-white border-emerald-600",
    red: "bg-red-600 text-white border-red-600",
  }[tone];
  return (
    <div className={`border ${cls} rounded-sm p-4`} data-testid={testid}>
      <div className={`text-[10px] uppercase tracking-[0.14em] font-semibold ${tone === "slate" ? "text-slate-500" : "text-white/70"}`}>{label}</div>
      <div className="font-display text-[22px] font-bold font-mono-num mt-1">{value ?? "—"}</div>
      {hint && <div className={`text-[10.5px] mt-0.5 ${tone === "slate" ? "text-slate-500" : "text-white/70"}`}>{hint}</div>}
    </div>
  );
}

function ProgressBar({ pct, tone = "slate" }) {
  const clamped = Math.max(0, Math.min(100, pct || 0));
  const color = { slate: "bg-slate-600", orange: "bg-[#EA580C]", dark: "bg-[#0F172A]", emerald: "bg-emerald-600", amber: "bg-amber-500", red: "bg-red-500" }[tone];
  return (
    <div className="h-1.5 w-full bg-slate-100 rounded-sm overflow-hidden">
      <div className={`h-full ${color}`} style={{ width: `${clamped}%` }} />
    </div>
  );
}

function statusRABill(status) {
  if (status === "paid") return "bg-emerald-100 text-emerald-700";
  if (status === "submitted") return "bg-orange-100 text-orange-700";
  return "bg-slate-100 text-slate-600";
}

function marginTone(pct, amount) {
  if (amount < 0) return "bg-red-100 text-red-700 border-red-200";
  if (pct >= 15) return "bg-emerald-100 text-emerald-700 border-emerald-200";
  if (pct >= 5) return "bg-amber-100 text-amber-700 border-amber-200";
  return "bg-slate-100 text-slate-600 border-slate-200";
}

function healthTone(score) {
  if (score >= 70) return "bg-emerald-500";
  if (score >= 40) return "bg-amber-500";
  return "bg-red-500";
}

function billedProgressTone(pct) {
  if (pct >= 75) return "emerald";
  if (pct >= 30) return "orange";
  return "slate";
}

function MarginBadge({ pct, amount }) {
  const isNeg = amount < 0;
  return (
    <span className={`inline-flex items-center gap-1 text-[10.5px] font-semibold rounded-sm px-1.5 py-[1px] border ${marginTone(pct, amount)}`}>
      {isNeg ? <TrendDown size={11} /> : <TrendUp size={11} />}
      {pct?.toFixed(1)}%
    </span>
  );
}

function HealthDot({ score }) {
  return <span title={`Health: ${score}/100`} className={`inline-block w-2 h-2 rounded-full ${healthTone(score)}`} />;
}

// ============================================================================
export default function ProjectPnL() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState("");
  const [detailPid, setDetailPid] = useState(null);
  const [statusFilter, setStatusFilter] = useState("all");

  const load = useCallback(async () => {
    setLoading(true);
    try { const { data } = await api.get("/projects-pnl-summary"); setData(data); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const rows = useMemo(() => {
    let r = data?.rows || [];
    if (q.trim()) {
      const s = q.trim().toLowerCase();
      r = r.filter((x) => (x.project_name || "").toLowerCase().includes(s) || (x.client_name || "").toLowerCase().includes(s) || (x.work_order_no || "").toLowerCase().includes(s));
    }
    if (statusFilter === "at_risk") r = r.filter((x) => x.gross_margin_pct < 5 && x.revenue_billed_taxable > 0);
    if (statusFilter === "healthy") r = r.filter((x) => x.gross_margin_pct >= 15 && x.revenue_billed_taxable > 0);
    if (statusFilter === "billed") r = r.filter((x) => x.revenue_billed_taxable > 0);
    return r.sort((a, b) => (b.revenue_billed_taxable || 0) - (a.revenue_billed_taxable || 0));
  }, [data, q, statusFilter]);

  const t = data?.totals;

  return (
    <div data-testid="project-pnl-page">
      <PageHeader
        title="Project P&L"
        module="Finance · Project Profitability"
        subtitle="Live per-project profitability across WO Value, RA billing, expenses, purchases, freight allocation, retention, ITC and milestone progress. Refresh anytime — numbers are computed on demand."
        ribbon={
          <BcRibbonGroup label="Process">
            <BcRibbonBtn icon={ArrowsClockwise} label={loading ? "Refreshing…" : "Refresh"} onClick={load} disabled={loading} testid="pnl-refresh" />
          </BcRibbonGroup>
        }
      />
      <div className="p-6 space-y-5">
        {t && (
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
            <KPI label="Projects" tone="dark" value={t.project_count} hint={t.at_risk_count ? `${t.at_risk_count} at risk` : "All healthy"} testid="k-projects" />
            <KPI label="Total WO Value" tone="slate" value={fmtMoney(t.wo_value)} testid="k-wo" />
            <KPI label="Revenue Billed" tone="orange" value={fmtMoney(t.revenue_billed_taxable)} hint={`${t.billed_pct_wo}% of WO`} testid="k-billed" />
            <KPI label="Collected" tone="emerald" value={fmtMoney(t.revenue_collected)} testid="k-collected" />
            <KPI label="Pending Payment" tone={t.amount_pending > 0 ? "orange" : "slate"} value={fmtMoney(t.amount_pending)} testid="k-pending" />
            <KPI label="Retention Held" tone="slate" value={fmtMoney(t.retention_held)} testid="k-retention" />
            <KPI label="Gross Margin" tone={t.gross_margin_amount >= 0 ? "emerald" : "red"} value={fmtMoney(t.gross_margin_amount)} hint={`${t.gross_margin_pct}%`} testid="k-margin" />
          </div>
        )}

        {/* Filters */}
        <div className="flex items-center gap-3 flex-wrap">
          <Input placeholder="Search project, client, WO#…" value={q} onChange={(e) => setQ(e.target.value)} className="w-72 h-9 rounded-sm" data-testid="pnl-search" />
          <div className="flex gap-1 border border-slate-200 rounded-sm p-0.5">
            {[["all", "All"], ["billed", "With Revenue"], ["healthy", "Healthy ≥15%"], ["at_risk", "At Risk <5%"]].map(([k, l]) => (
              <button key={k} onClick={() => setStatusFilter(k)} className={`text-[11px] px-2.5 py-1 rounded-sm ${statusFilter === k ? "bg-[#EA580C] text-white" : "text-slate-700 hover:bg-slate-100"}`} data-testid={`pnl-filter-${k}`}>{l}</button>
            ))}
          </div>
          <div className="ml-auto text-[11.5px] text-slate-500 font-mono-num">{rows.length} project(s)</div>
        </div>

        {rows.length === 0 ? (
          <EmptyState title="No projects match" description="Try a different filter or search term." />
        ) : (
          <div className="border border-slate-200 rounded-sm overflow-hidden bc-card">
            <Table className="bc-list">
              <TableHeader>
                <TableRow>
                  <TableHead className="w-6"></TableHead>
                  <TableHead>Project</TableHead>
                  <TableHead>Client</TableHead>
                  <TableHead>WO#</TableHead>
                  <TableHead className="text-right">WO Value</TableHead>
                  <TableHead className="text-right">Billed</TableHead>
                  <TableHead className="w-24">Billed %WO</TableHead>
                  <TableHead className="text-right">Collected</TableHead>
                  <TableHead className="text-right">Retention</TableHead>
                  <TableHead className="text-right">Costs</TableHead>
                  <TableHead className="text-right">Margin</TableHead>
                  <TableHead className="w-32">Progress (S/I/T)</TableHead>
                  <TableHead className="w-10"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((r) => (
                  <TableRow key={r.project_id} className="cursor-pointer hover:bg-slate-50" onClick={() => setDetailPid(r.project_id)} data-testid={`pnl-row-${r.project_id}`}>
                    <TableCell><HealthDot score={r.health_score} /></TableCell>
                    <TableCell className="font-semibold">{r.project_name}</TableCell>
                    <TableCell className="text-[11.5px] text-slate-600">{r.client_name || "—"}</TableCell>
                    <TableCell className="font-mono text-[11px]">{r.work_order_no || "—"}</TableCell>
                    <TableCell className="text-right font-mono-num">{fmtMoney(r.wo_value)}</TableCell>
                    <TableCell className="text-right font-mono-num font-semibold">{fmtMoney(r.revenue_billed_taxable)}</TableCell>
                    <TableCell className="w-24"><div className="flex items-center gap-1"><ProgressBar pct={r.billed_pct_wo} tone={billedProgressTone(r.billed_pct_wo)} /><span className="text-[10px] font-mono-num text-slate-600 w-10 text-right">{r.billed_pct_wo?.toFixed(0)}%</span></div></TableCell>
                    <TableCell className="text-right font-mono-num">{fmtMoney(r.revenue_collected)}</TableCell>
                    <TableCell className="text-right font-mono-num text-slate-600">{fmtMoney(r.retention_held)}</TableCell>
                    <TableCell className="text-right font-mono-num text-slate-700">{fmtMoney(r.total_cost)}</TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-2">
                        <span className={`font-mono-num font-bold ${r.gross_margin_amount >= 0 ? "text-emerald-700" : "text-red-700"}`}>{fmtMoney(r.gross_margin_amount)}</span>
                        <MarginBadge pct={r.gross_margin_pct} amount={r.gross_margin_amount} />
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="space-y-0.5">
                        <div className="flex items-center gap-1"><span className="text-[9px] text-slate-500 w-3">S</span><ProgressBar pct={r.supply_progress_pct} tone="dark" /><span className="text-[9px] font-mono-num text-slate-500 w-7 text-right">{r.supply_progress_pct?.toFixed(0)}%</span></div>
                        <div className="flex items-center gap-1"><span className="text-[9px] text-slate-500 w-3">I</span><ProgressBar pct={r.install_progress_pct} tone="orange" /><span className="text-[9px] font-mono-num text-slate-500 w-7 text-right">{r.install_progress_pct?.toFixed(0)}%</span></div>
                        <div className="flex items-center gap-1"><span className="text-[9px] text-slate-500 w-3">T</span><ProgressBar pct={r.tnc_progress_pct} tone="emerald" /><span className="text-[9px] font-mono-num text-slate-500 w-7 text-right">{r.tnc_progress_pct?.toFixed(0)}%</span></div>
                      </div>
                    </TableCell>
                    <TableCell><ArrowRight size={13} className="text-slate-400" /></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </div>

      {detailPid && <ProjectPnLDetail projectId={detailPid} onClose={() => setDetailPid(null)} />}
    </div>
  );
}

// ============================================================================
// Detail drawer
// ============================================================================
function ProjectPnLDetail({ projectId, onClose }) {
  const [d, setD] = useState(null);
  useEffect(() => { (async () => {
    try { const { data } = await api.get(`/projects/${projectId}/pnl`); setD(data); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  })(); }, [projectId]);

  if (!d) return null;
  return (
    <Sheet open onOpenChange={(v) => !v && onClose()}>
      <SheetContent className="w-[min(960px,100vw)] sm:max-w-none overflow-y-auto" data-testid="pnl-detail-sheet">
        <SheetHeader className="mb-4">
          <SheetTitle className="font-display flex items-center gap-2">
            <ChartLine size={18} className="text-[#EA580C]" /> {d.project_name} · P&L
          </SheetTitle>
          <SheetDescription className="text-[11.5px] text-slate-500">Live profitability breakdown — Work Order value vs Revenue billed vs Collected vs Costs, milestone progress, RA bills and expense categories.</SheetDescription>
        </SheetHeader>

        <div className="space-y-5">
          {/* KPI row */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <KPI label="WO Value" tone="slate" value={fmtMoney(d.wo_value)} hint={d.work_order_no} testid="d-wo" />
            <KPI label="Revenue Billed" tone="orange" value={fmtMoney(d.revenue_billed_taxable)} hint={`${d.billed_pct_wo}% of WO`} testid="d-billed" />
            <KPI label="Collected" tone="emerald" value={fmtMoney(d.revenue_collected)} hint={`${d.collected_pct_billed}% of billed`} testid="d-collected" />
            <KPI label="Gross Margin" tone={d.gross_margin_amount >= 0 ? "emerald" : "red"} value={fmtMoney(d.gross_margin_amount)} hint={`${d.gross_margin_pct}%`} testid="d-margin" />
            <KPI label="Retention Held" tone="dark" value={fmtMoney(d.retention_held)} testid="d-retention" />
            <KPI label="TDS Deducted" tone="slate" value={fmtMoney(d.tds_deducted)} testid="d-tds" />
            <KPI label="Costs" tone="slate" value={fmtMoney(d.total_cost)} hint={`exp ${fmtMoney(d.cost_expenses)} + pur ${fmtMoney(d.cost_purchases)}`} testid="d-costs" />
            <KPI label="ITC" tone="slate" value={fmtMoney(d.itc_total)} hint={`sup+ins+tnc`} testid="d-itc" />
          </div>

          {/* Milestone progress */}
          <div className="border border-slate-200 rounded-sm bg-white p-4 space-y-3">
            <div className="text-[11px] font-semibold uppercase tracking-widest text-slate-600">Milestone billed vs BOQ total (₹{d.boq_total_value?.toLocaleString("en-IN")})</div>
            {[
              { label: "Supply", pct: d.supply_progress_pct, amt: d.supply_billed_amount, itc: d.itc_supply, tone: "dark" },
              { label: "Installation", pct: d.install_progress_pct, amt: d.install_billed_amount, itc: d.itc_install, tone: "orange" },
              { label: "Testing / Commissioning / Handover", pct: d.tnc_progress_pct, amt: d.tnc_billed_amount, itc: d.itc_tnc, tone: "emerald" },
            ].map((m) => (
              <div key={m.label} className="grid grid-cols-[130px_1fr_100px_100px] items-center gap-3">
                <div className="text-[12.5px] font-semibold text-slate-800">{m.label}</div>
                <ProgressBar pct={m.pct} tone={m.tone} />
                <div className="text-right font-mono-num text-[12px]">{fmtMoney(m.amt)}</div>
                <div className="text-right font-mono-num text-[11px] text-slate-500">ITC {fmtMoney(m.itc)}</div>
              </div>
            ))}
          </div>

          {/* RA bill list */}
          <div className="border border-slate-200 rounded-sm bg-white">
            <div className="px-3 py-2 text-[11px] font-semibold uppercase tracking-widest text-slate-600 border-b border-slate-200">RA Bills · {d.ra_bill_count} (paid: {d.ra_paid_count})</div>
            {(d.ra_bills || []).length === 0 ? (
              <div className="p-6 text-center text-[12px] text-slate-400">No RA bills yet against this project.</div>
            ) : (
              <Table className="bc-list">
                <TableHeader><TableRow>
                  <TableHead>RA #</TableHead><TableHead>Date</TableHead>
                  <TableHead className="text-right">This Bill</TableHead>
                  <TableHead className="text-right">Payable</TableHead>
                  <TableHead>Status</TableHead>
                </TableRow></TableHeader>
                <TableBody>
                  {d.ra_bills.map((r) => (
                    <TableRow key={r.id}>
                      <TableCell className="font-mono font-semibold">{r.ra_bill_no}{r.is_final_bill ? " (FINAL)" : ""}</TableCell>
                      <TableCell>{r.bill_date ? fmtDate(r.bill_date) : "—"}</TableCell>
                      <TableCell className="text-right font-mono-num">{fmtMoney(r.gross_value_this_bill)}</TableCell>
                      <TableCell className="text-right font-mono-num font-semibold text-emerald-700">{fmtMoney(r.amount_to_be_processed)}</TableCell>
                      <TableCell><span className={`inline-block text-[10px] uppercase font-semibold rounded-sm px-1.5 py-[1px] ${statusRABill(r.status)}`}>{r.status}</span></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </div>

          {/* Cost breakdown */}
          <div className="border border-slate-200 rounded-sm bg-white">
            <div className="px-3 py-2 text-[11px] font-semibold uppercase tracking-widest text-slate-600 border-b border-slate-200">Cost breakdown · ₹{d.total_cost?.toLocaleString("en-IN")}</div>
            <div className="p-3 grid grid-cols-2 gap-4">
              <div>
                <div className="text-[10.5px] uppercase tracking-widest text-slate-500 mb-1">Expenses by category</div>
                {(d.expense_by_category || []).length === 0 ? (
                  <div className="text-[11.5px] text-slate-400">No expenses recorded.</div>
                ) : (
                  <div className="space-y-1">
                    {d.expense_by_category.map((e) => (
                      <div key={e.category} className="flex items-center justify-between text-[12px]">
                        <span className="capitalize">{e.category}</span>
                        <span className="font-mono-num text-slate-700">{fmtMoney(e.amount)}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div>
                <div className="text-[10.5px] uppercase tracking-widest text-slate-500 mb-1">Purchases</div>
                <div className="flex items-center justify-between text-[13px]"><span>{d.purchase_count} vendor bill(s)</span><span className="font-mono-num font-semibold">{fmtMoney(d.cost_purchases)}</span></div>
                <div className="text-[10.5px] text-slate-400 mt-1">Includes auto-allocated freight, insurance, packing, other charges.</div>
              </div>
            </div>
          </div>

          <div className="flex justify-end pt-4 border-t border-slate-200 sticky bottom-0 bg-white">
            <Button variant="outline" onClick={onClose} className="rounded-sm">Close</Button>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
